import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";

import UserInfo from "src/components/common/userInfo";
import { testerEmails } from "src/components/common/functions";
import { useAuthStore, User } from "../main/authStore";
import { Vendor } from "./types";

type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};

export default function VendorDetail() {
    const user = useAuthStore(state => state.user) as User;
    const { vendor } = useRouteLoaderData("vendor") as { vendor: Vendor };
    const submit = useSubmit();

    const handleDelete = (id: number | null) => {
        if (id === null) return;
        if (!window.confirm("Are you sure to delete this vendor?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete",
        });
    };

    const handleValidate = (id: number | null) => {
        if (id === null) return;
        if (!window.confirm("Are you sure to validate this vendor?")) return;
        let validateReason = window.prompt("reason to validate:");
        if (validateReason === null) return;
        while (validateReason === "") {
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if (validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate",
        });
    };

    const handleVoid = (id: number | null) => {
        if (id === null) return;
        if (!window.confirm("Are you sure to void this vendor?")) return;
        let voidReason = window.prompt("reason to void:");
        if (voidReason === null) return;
        while (voidReason === "") {
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if (voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void",
        });
    };

    const address = [vendor.street, vendor.city, vendor.state, vendor.zipCode, vendor.country].filter(Boolean).join(", ");
    const fields: FieldDef[] = [
        { label: "Vendor ID", value: vendor.id },
        { label: "Vendor Name", value: vendor.vendorName },
        {
            label: "Website",
            value: vendor.website ? <a href = {vendor.website} target = "_blank" rel = "noreferrer">{vendor.website}</a> : null,
        },
        {
            label: "Business Phone",
            value: vendor.phoneNumber ? <a href = {`tel:+1${vendor.phoneNumber.replace(/-/g, "")}${vendor.extension ? `,${vendor.extension}` : ""}`}>{vendor.phoneNumber}{vendor.extension ? ` Ext. ${vendor.extension}` : null}</a> : null,
        },
        {
            label: "Fax",
            value: vendor.fax ? <a href = {"fax:+1" + vendor.fax.replace(/-/g, "")}>{vendor.fax}</a> : null,
        },
        {
            label: "Address",
            value: address ? <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target = "_blank" rel = "noreferrer">{address}</a> : null,
        },
        {
            label: "Creator",
            show: !!vendor.creatorId,
            value: (
                <>
                    <UserInfo name = {vendor.creatorName} id = {vendor.creatorId} />
                    <span className = "ms-2 text-muted">{vendor.createdAt}</span>
                </>
            ),
        },
        {
            label: "Updater",
            show: !!vendor.updaterId,
            value: (
                <>
                    <UserInfo name = {vendor.updaterName} id = {vendor.updaterId} />
                    <span className = "ms-2 text-muted">{vendor.updatedAt}</span>
                </>
            ),
        },
        {
            label: "Void Reason",
            show: !!vendor.voidReason,
            value: vendor.voidReason,
        },
        {
            label: "Validation Reason",
            show: !!vendor.validateReason,
            value: vendor.validateReason,
        },
    ];

    return (
        <div>
            <div className = "cssSeperateWhenWide ps-3 pt-3">
                <table className = "table table-fixed flex-grow-1 maxWidth-50 me-3">
                    <tbody className = "noTableTopBorder">
                        {fields.filter(field => field.value && field.show !== false).map((field, idx) => (
                            <tr key = {idx}>
                                <th>{field.label}</th>
                                <td>{field.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className = "d-flex flex-column flex-grow-1 maxWidth-50 me-3">
                    <fieldset className = "border rounded flex-grow-1 px-3">
                        <legend className = "fw-bold">Background</legend>
                        <span className = "cssNotes">{vendor.background}</span>
                    </fieldset>
                </div>
            </div>
            <div className = "my-3">
                {vendor.void === "yes" ? (
                    <>
                        {testerEmails.includes(user.email) ? (
                            <button className = "btn btn-primary" onClick = {() => handleValidate(vendor.id)}>Validate</button>
                        ) : null}
                    </>
                ) : (
                    <>
                        <Link to = "Edit" className = "btn btn-outline-primary ms-3">Edit</Link>
                        <button type = "button" className = "btn btn-danger ms-3" onClick = {() => handleVoid(vendor.id)}>Void</button>
                    </>
                )}
                {vendor.void === "yes" ? (
                    <button className = "btn btn-danger ms-3" onClick = {() => handleDelete(vendor.id)}>Delete</button>
                ) : null}
                <Link to = ".." relative = "path" className = "btn btn-outline-secondary float-end">Back</Link>
            </div>
        </div>
    );
}
